/**
 * Fire the semantic-search NDJSON request the moment the user *commits* to a
 * query (Enter, click on a trending chip, example prompt, etc.) — before the
 * route transition even completes.
 *
 * Two outputs:
 *   1. Writes the final result set into the localStorage search cache, so when
 *      <SearchResults /> mounts it gets a FRESH hit and skips the network.
 *   2. Tracks an in-flight promise per (query, fingerprint) so a mounting
 *      <SearchResults /> can await the already-running fetch instead of
 *      kicking off a duplicate one.
 *
 * Supersession: when the user commits a new query before the previous prefetch
 * resolves, the older in-flight request is aborted so we stop burning network
 * and CPU on a result the user will never see.
 *
 * The fetch logic mirrors SearchResults' own streaming reader. Kept here as
 * fire-and-forget (never throws to the caller).
 */
import { supabase } from "@/integrations/supabase/client";
import { getStoredTuning } from "@/components/admin/AdminScoreTuning";
import {
  fingerprintTuning,
  setCachedSearch,
  getCachedSearch,
} from "@/lib/searchCache";
import { getSessionId } from "@/lib/sessionId";

export type PrefetchPayload = {
  results: any[];
  theme: string;
  matched_answer: any | null;
};

type PendingEntry = {
  promise: Promise<PrefetchPayload | null>;
  controller: AbortController;
};

const pending = new Map<string, PendingEntry>();

function keyFor(query: string, fingerprint: string): string {
  return `${query.trim().toLowerCase().replace(/\s+/g, " ")}::${fingerprint}`;
}

async function runPrefetch(
  query: string,
  fingerprint: string,
  tuning: unknown,
  signal: AbortSignal,
): Promise<PrefetchPayload | null> {
  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
  const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

  const { data: sessionData } = await supabase.auth.getSession();
  if (signal.aborted) return null;
  const accessToken = sessionData?.session?.access_token || SUPABASE_KEY;

  const resp = await fetch(`${SUPABASE_URL}/functions/v1/semantic-search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_KEY,
    },
    body: JSON.stringify({
      query,
      tuning,
      session_id: getSessionId(),
      stream: true,
    }),
    signal,
  });

  if (!resp.ok || !resp.body) return null;

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalEvent: any = null;

  try {
    while (true) {
      if (signal.aborted) {
        try { await reader.cancel(); } catch { /* noop */ }
        return null;
      }
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (!line) continue;
        try {
          const evt = JSON.parse(line);
          if (evt.type === "final") finalEvent = evt;
        } catch { /* tolerate partial lines */ }
      }
    }
  } catch (err: any) {
    if (err?.name === "AbortError") return null;
    return null;
  }

  if (!finalEvent?.results?.length) return null;

  const payload: PrefetchPayload = {
    results: finalEvent.results,
    theme: finalEvent.theme || "",
    matched_answer: finalEvent.matched_answer || null,
  };

  try {
    setCachedSearch(query, payload, { fingerprint });
  } catch { /* cache writes never throw */ }

  return payload;
}

/**
 * Abort every in-flight prefetch whose key !== keepKey. Called whenever a new
 * query is committed so superseded requests stop wasting bandwidth.
 */
function abortOthers(keepKey: string | null): void {
  for (const [k, entry] of pending) {
    if (k === keepKey) continue;
    try { entry.controller.abort(); } catch { /* noop */ }
    pending.delete(k);
  }
}

/**
 * Fire-and-forget warm. Safe to call from click handlers; never throws.
 * No-ops if a fresh cache entry already exists. If an identical request is
 * already in flight, returns without starting a new one (but still cancels
 * any other in-flight prefetches for different queries).
 */
export function prefetchSearch(query: string): void {
  const trimmed = query?.trim();
  if (!trimmed || typeof window === "undefined") return;

  let fingerprint = "0";
  let tuning: unknown = {};
  try {
    tuning = getStoredTuning();
    fingerprint = fingerprintTuning(tuning);
  } catch { /* tuning is best-effort */ }

  // Already in cache and fresh — nothing to do (but cancel other in-flights).
  try {
    const hit = getCachedSearch(trimmed, fingerprint);
    if (hit?.freshness === "fresh") {
      abortOthers(null);
      return;
    }
  } catch { /* ignore */ }

  const key = keyFor(trimmed, fingerprint);

  // Supersede any older prefetches that no longer match the active query.
  abortOthers(key);

  if (pending.has(key)) return;

  const controller = new AbortController();
  const promise = runPrefetch(trimmed, fingerprint, tuning, controller.signal)
    .catch(() => null)
    .finally(() => {
      // Hold the entry briefly so SearchResults can still consume it after
      // mount; then clear so a future identical search can re-prefetch.
      setTimeout(() => {
        const current = pending.get(key);
        if (current && current.controller === controller) {
          pending.delete(key);
        }
      }, 30_000);
    });

  pending.set(key, { promise, controller });
}

/**
 * Debounced + throttled variant for chip/prompt taps. Rapid taps coalesce
 * into a single trailing prefetch for the most recent query, and starts are
 * spaced by `minIntervalMs` so a flurry of taps cannot fan out into a flurry
 * of network requests.
 */
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastQueued: string | null = null;
let lastStartedAt = 0;
const DEFAULT_DEBOUNCE_MS = 140;
const MIN_INTERVAL_MS = 250;

export function prefetchSearchDebounced(
  query: string,
  delayMs: number = DEFAULT_DEBOUNCE_MS,
): void {
  const trimmed = query?.trim();
  if (!trimmed) return;
  lastQueued = trimmed;

  if (debounceTimer) clearTimeout(debounceTimer);

  const sinceLast = Date.now() - lastStartedAt;
  const wait = Math.max(delayMs, MIN_INTERVAL_MS - sinceLast);

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    const q = lastQueued;
    lastQueued = null;
    if (!q) return;
    lastStartedAt = Date.now();
    prefetchSearch(q);
  }, wait);
}

/**
 * Explicitly cancel every in-flight prefetch and any pending debounced call.
 * Useful when the user clears the search bar or navigates away.
 */
export function cancelAllPrefetches(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  lastQueued = null;
  abortOthers(null);
}

/**
 * If an in-flight prefetch exists for this (query, fingerprint), return its
 * promise so the caller can await it instead of starting a duplicate fetch.
 */
export function getPendingSearch(
  query: string,
  fingerprint: string,
): Promise<PrefetchPayload | null> | null {
  const trimmed = query?.trim();
  if (!trimmed) return null;
  return pending.get(keyFor(trimmed, fingerprint))?.promise ?? null;
}
